import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Divider,
} from "@mui/material";
import { Autocomplete } from "@mui/material";
import {
  Add,
  Search,
  Refresh,
  Notifications,
  Close,
  Edit,
  Delete,
  Visibility,
  Send,
} from "@mui/icons-material";
import axios from "axios";
import { BrandConfirmDialog } from "../../components/modals/wizard/BrandDialogs";

const API_ROOT =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000/api";
const API_BASE = `${String(API_ROOT).replace(/\/+$/, "")}/announcements`;

const TYPES = [
  { value: "info", label: "Information" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warning" },
  { value: "error", label: "Error" },
];

const TARGET_SCOPES = [
  { value: "hierarchy", label: "My Hierarchy (Default)" },
  { value: "all", label: "Everyone (Admin Only)" },
  { value: "specific", label: "Specific Users" },
];

const DISPLAY_POSITIONS = [
  { value: "top", label: "Top (Slide Down)" },
  { value: "center", label: "Center (Default)" },
  { value: "bottom", label: "Bottom (Slide Up)" },
];

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function chipColorByType(type) {
  const t = (type || "info").toLowerCase();
  if (t === "success") return "success";
  if (t === "warning") return "warning";
  if (t === "error") return "error";
  return "info";
}

function safeToLocal(date) {
  try {
    return date ? new Date(date).toLocaleString() : "—";
  } catch {
    return "—";
  }
}

function toDatetimeLocalValue(isoOrDate) {
  if (!isoOrDate) return "";
  try {
    const d = new Date(isoOrDate);
    if (!Number.isFinite(d.getTime())) return "";
    // convert to local datetime-local format: YYYY-MM-DDTHH:mm
    const pad = (n) => String(n).padStart(2, "0");
    const y = d.getFullYear();
    const m = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${y}-${m}-${day}T${hh}:${mm}`;
  } catch {
    return "";
  }
}

function summarizeTarget(a) {
  const scope = (a?.targetScope || "hierarchy").toUpperCase();
  const roles = Array.isArray(a?.targetRoles) ? a.targetRoles : [];
  const users = Array.isArray(a?.targetUsers) ? a.targetUsers : [];
  const rolesLabel = roles.length ? `Roles: ${roles.length}` : "Roles: —";
  const usersLabel = users.length ? `Users: ${users.length}` : "Users: —";
  return `${scope} • ${rolesLabel} • ${usersLabel}`;
}

export default function Announcements() {
  const [showModal, setShowModal] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const [list, setList] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const [userRole, setUserRole] = useState("");
  const [allowedRoles, setAllowedRoles] = useState([]);

  // ✅ specific users selection
  const [targetUsersOptions, setTargetUsersOptions] = useState([]); // [{_id, username, role}]
  const [targetUsersLoading, setTargetUsersLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]); // same shape as options

  // ✅ editing
  const [editId, setEditId] = useState(null);
  const [currentRow, setCurrentRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);

  // Form
  const [title, setTitle] = useState("Announcement");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const [priority, setPriority] = useState(1);
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [targetScope, setTargetScope] = useState("hierarchy");
  const [specificRoles, setSpecificRoles] = useState([]);
  const [displayPosition, setDisplayPosition] = useState("center");

  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const canBroadcastAll =
    userRole === "admin" ||
    userRole === "manager" ||
    userRole === "manager";

  const specificPickerLabel = useMemo(() => {
    const r = (userRole || "").toLowerCase();
    if (r === "sub_admin") return "Select Dealer(s) under you";
    if (r === "group_admin") return "Select User(s) under you";
    if (r === "admin" || r === "manager") return "Select User(s)";
    return "Select Target User(s)";
  }, [userRole]);

  // ✅ fetch ALL for management table (more detail)
  const fetchAll = useCallback(async () => {
    try {
      setPageLoading(true);
      const res = await axios.get(`${API_BASE}/all?limit=50&page=1`, {
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setList(Array.isArray(res.data.announcements) ? res.data.announcements : []);
      } else {
        setList([]);
      }
    } catch {
      setToast("Failed to load announcements");
      setList([]);
    } finally {
      setPageLoading(false);
    }
  }, []);

  const fetchAllowedRoles = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/allowed-roles`, {
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setAllowedRoles(
          Array.isArray(res.data.allowedRoles) ? res.data.allowedRoles : []
        );
        setUserRole(res.data.userRole || "");
      }
    } catch {
      // non-blocking
    }
  }, []);

  // ✅ target users list for "specific"
  const fetchTargetUsers = useCallback(async () => {
    try {
      setTargetUsersLoading(true);
      const res = await axios.get(`${API_BASE}/target-users`, {
        headers: getAuthHeaders(),
      });

      if (res.data?.success) {
        setTargetUsersOptions(Array.isArray(res.data.users) ? res.data.users : []);
      } else {
        setTargetUsersOptions([]);
      }
    } catch (e) {
      setTargetUsersOptions([]);
      setToast(e?.response?.data?.message || "Failed to load target users");
    } finally {
      setTargetUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchAllowedRoles();
  }, [fetchAll, fetchAllowedRoles]);

  // auto-load user list for "specific" mode
  useEffect(() => {
    if (showModal && targetScope === "specific") {
      fetchTargetUsers();
    }
  }, [showModal, targetScope, fetchTargetUsers]);

  const filtered = useMemo(() => {
    const s = (q || "").toLowerCase().trim();
    if (!s) return list;
    return list.filter((a) => {
      const content = `${a.title || ""} ${a.message || ""} ${a.createdBy?.username || ""} ${
        a.creatorRole || ""
      } ${a.targetScope || ""}`.toLowerCase();
      return content.includes(s);
    });
  }, [q, list]);

  const resetForm = () => {
    setEditId(null);
    setTitle("Announcement");
    setMessage("");
    setType("info");
    setPriority(1);
    setStartAt("");
    setEndAt("");
    setTargetScope("hierarchy");
    setSpecificRoles([]);
    setSelectedUsers([]);
    setDisplayPosition("center");
    setError("");
  };

  const validate = () => {
    if (!title.trim()) return "Title is required";
    if (!message.trim()) return "Message is required";

    if (startAt && endAt) {
      const s = new Date(startAt);
      const e = new Date(endAt);
      if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) {
        return "Start/End time is invalid";
      }
      if (e < s) return "End time cannot be before start time";
    }

    if (targetScope === "all" && !canBroadcastAll) {
      return "Only Admin/Manager can broadcast to everyone";
    }

    if (targetScope === "hierarchy" && specificRoles.length && !allowedRoles.length) {
      return "Allowed roles not loaded yet. Please refresh.";
    }

    if (targetScope === "specific" && (!selectedUsers || selectedUsers.length === 0)) {
      return "Please select at least one target user";
    }

    if (!["top", "center", "bottom"].includes(displayPosition)) {
      return "Display position is invalid";
    }

    return "";
  };

  const buildTriggerPayload = () => ({
    title: title.trim(),
    message: message.trim(),
    type,
    priority: Number(priority) || 1,
    targetScope,
    displayPosition,
    specificRoles:
      targetScope === "hierarchy" && specificRoles.length ? specificRoles : undefined,
    specificUserIds:
      targetScope === "specific" ? selectedUsers.map((u) => u._id) : undefined,
    startAt: startAt ? new Date(startAt).toISOString() : null,
    endAt: endAt ? new Date(endAt).toISOString() : null,
  });

  /**
   * ✅ Create
   * ✅ Edit: PUT update + then POST trigger again (re-broadcast)
   */
  const handleSave = async () => {
    const v = validate();
    if (v) return setError(v);

    setSaving(true);
    setError("");

    try {
      const payload = buildTriggerPayload();

      if (!editId) {
        // CREATE => trigger
        const res = await axios.post(`${API_BASE}/trigger`, payload, {
          headers: getAuthHeaders(),
        });

        if (res.data?.success) {
          setToast(`Announcement sent to ${res.data.targetCount} users`);
          setShowModal(false);
          resetForm();
          fetchAll();
        } else {
          setError(res.data?.message || "Failed to save");
        }
      } else {
        // UPDATE => update record + re-trigger
        // 1) update existing record
        const upd = await axios.put(
          `${API_BASE}/${editId}`,
          {
            title: payload.title,
            message: payload.message,
            type: payload.type,
            priority: payload.priority,
            targetScope: payload.targetScope,
            displayPosition: payload.displayPosition,
            targetRoles: payload.specificRoles, // optional; backend uses updates directly
            targetUsers: payload.specificUserIds,
            startAt: payload.startAt,
            endAt: payload.endAt,
            isActive: true,
          },
          { headers: getAuthHeaders() }
        );

        if (!upd.data?.success) {
          throw new Error(upd.data?.message || "Update failed");
        }

        // 2) re-broadcast (new announcement event)
        const trig = await axios.post(`${API_BASE}/trigger`, payload, {
          headers: getAuthHeaders(),
        });

        if (trig.data?.success) {
          setToast(`Updated + re-sent to ${trig.data.targetCount} targets`);
          setShowModal(false);
          resetForm();
          fetchAll();
        } else {
          setError(trig.data?.message || "Updated but re-trigger failed");
        }
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role) => {
    setSpecificRoles((prev) =>
      prev.includes(role) ? prev.filter((x) => x !== role) : [...prev, role]
    );
  };

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openDetails = (row) => {
    setCurrentRow(row);
    setDetailOpen(true);
  };

  const openEdit = async (row) => {
    resetForm();
    setEditId(row._id);

    setTitle(row.title || "");
    setMessage(row.message || "");
    setType(row.type || "info");
    setPriority(Number(row.priority) || 1);
    setDisplayPosition((row.displayPosition || "center").toLowerCase());
    setTargetScope(row.targetScope || "hierarchy");

    setStartAt(toDatetimeLocalValue(row.startAt));
    setEndAt(toDatetimeLocalValue(row.endAt));

    // prefill roles/users (best effort)
    const roles = Array.isArray(row.targetRoles) ? row.targetRoles : [];
    setSpecificRoles(roles);

    // for users, need options list to map ids -> objects
    if ((row.targetScope || "") === "specific") {
      await fetchTargetUsers();
      const ids = (Array.isArray(row.targetUsers) ? row.targetUsers : []).map((u) =>
        typeof u === "string" ? u : u?._id
      );
      const mapped = targetUsersOptions.filter((u) => ids.includes(u._id));
      setSelectedUsers(mapped);
    }

    setShowModal(true);
  };

  const confirmDelete = (row) => {
    setDeleteRow(row);
    setConfirmDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteRow?._id) return;
    try {
      await axios.delete(`${API_BASE}/${deleteRow._id}`, {
        headers: getAuthHeaders(),
      });
      setToast("Announcement deleted");
      setConfirmDeleteOpen(false);
      setDeleteRow(null);
      fetchAll();
    } catch (e) {
      setToast(e?.response?.data?.message || "Delete failed");
    }
  };

  const quickResend = async (row) => {
    try {
      const payload = {
        title: (row.title || "").trim() || "Announcement",
        message: (row.message || "").trim() || "—",
        type: row.type || "info",
        priority: Number(row.priority) || 1,
        targetScope: row.targetScope || "hierarchy",
        displayPosition: row.displayPosition || "center",
        specificRoles:
          row.targetScope === "hierarchy" && Array.isArray(row.targetRoles) && row.targetRoles.length
            ? row.targetRoles
            : undefined,
        specificUserIds:
          row.targetScope === "specific" && Array.isArray(row.targetUsers) && row.targetUsers.length
            ? row.targetUsers.map((u) => (typeof u === "string" ? u : u?._id))
            : undefined,
        startAt: row.startAt ? new Date(row.startAt).toISOString() : null,
        endAt: row.endAt ? new Date(row.endAt).toISOString() : null,
      };

      const res = await axios.post(`${API_BASE}/trigger`, payload, {
        headers: getAuthHeaders(),
      });
      if (res.data?.success) {
        setToast(`Re-sent to ${res.data.targetCount} targets`);
        fetchAll();
      } else {
        setToast(res.data?.message || "Re-send failed");
      }
    } catch (e) {
      setToast(e?.response?.data?.message || "Re-send failed");
    }
  };

  return (
    <Box p={3}>
      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast("")}>
        <Alert severity="success" onClose={() => setToast("")}>
          {toast}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Notifications />
            <Box>
              <Typography variant="h6">Broadcasts</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage announcements (Create / Update / Re-send / Delete)
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search title/message/creator..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { md: 320 } }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAll}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Create
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Table (detailed) */}
      <Paper sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 340 }}>Content</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Position</TableCell>
              <TableCell sx={{ minWidth: 220 }}>Target</TableCell>
              <TableCell sx={{ minWidth: 200 }}>Schedule</TableCell>
              <TableCell>Creator</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right" sx={{ minWidth: 170 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageLoading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : filtered.length ? (
              filtered.map((a) => (
                <TableRow key={a._id} hover>
                  <TableCell>
                    <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                      {a.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {a.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.8 }}>
                      ID: {a._id}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={(a.type || "info").toUpperCase()}
                      color={chipColorByType(a.type)}
                      size="small"
                      variant="filled"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={`P${Number(a.priority) || 1}`}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={(a.displayPosition || "center").toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {(a.targetScope || "hierarchy").toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Roles: {Array.isArray(a.targetRoles) ? a.targetRoles.length : 0} • Users:{" "}
                      {Array.isArray(a.targetUsers) ? a.targetUsers.length : 0}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">
                      Start: {safeToLocal(a.startAt)}
                    </Typography>
                    <Typography variant="body2">
                      End: {safeToLocal(a.endAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Created: {safeToLocal(a.createdAt)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {a.createdBy?.username || "Unknown"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.creatorRole || a.createdBy?.role || "—"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={a.isActive ? "Live" : "Inactive"}
                      color={a.isActive ? "success" : "default"}
                      size="small"
                      variant={a.isActive ? "filled" : "outlined"}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="View details">
                      <IconButton onClick={() => openDetails(a)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit (Update + Re-send)">
                      <IconButton onClick={() => openEdit(a)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Re-send now">
                      <IconButton onClick={() => quickResend(a)}>
                        <Send />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton color="error" onClick={() => confirmDelete(a)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No records found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Announcement Details
          <IconButton
            onClick={() => setDetailOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!currentRow ? (
            <Typography>—</Typography>
          ) : (
            <Box>
              <Stack spacing={1}>
                <Typography variant="h6" fontWeight={800}>
                  {currentRow.title}
                </Typography>
                <Typography color="text.secondary">{currentRow.message}</Typography>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Type</Typography>
                    <Chip
                      label={(currentRow.type || "info").toUpperCase()}
                      color={chipColorByType(currentRow.type)}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Priority</Typography>
                    <Typography> {Number(currentRow.priority) || 1} </Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2">Display Position</Typography>
                    <Typography>{(currentRow.displayPosition || "center").toUpperCase()}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Target Summary</Typography>
                    <Typography>{summarizeTarget(currentRow)}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Creator</Typography>
                    <Typography>
                      {currentRow.createdBy?.username || "Unknown"} ({currentRow.creatorRole || currentRow.createdBy?.role || "—"})
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Start / End</Typography>
                    <Typography>Start: {safeToLocal(currentRow.startAt)}</Typography>
                    <Typography>End: {safeToLocal(currentRow.endAt)}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2">Meta</Typography>
                    <Typography>ID: {currentRow._id}</Typography>
                    <Typography>Created: {safeToLocal(currentRow.createdAt)}</Typography>
                    <Typography>Updated: {safeToLocal(currentRow.updatedAt)}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Target Roles</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                      {(Array.isArray(currentRow.targetRoles) ? currentRow.targetRoles : []).length ? (
                        currentRow.targetRoles.map((r) => (
                          <Chip key={r} label={r} size="small" variant="outlined" />
                        ))
                      ) : (
                        <Typography color="text.secondary">—</Typography>
                      )}
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Target Users</Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
                      {(Array.isArray(currentRow.targetUsers) ? currentRow.targetUsers : []).length ? (
                        currentRow.targetUsers.map((u) => {
                          const id = typeof u === "string" ? u : u?._id;
                          const name = typeof u === "string" ? u : u?.username || u?._id;
                          return <Chip key={id} label={name} size="small" variant="outlined" />;
                        })
                      ) : (
                        <Typography color="text.secondary">—</Typography>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          {currentRow && (
            <>
              <Button variant="outlined" startIcon={<Send />} onClick={() => quickResend(currentRow)}>
                Re-send
              </Button>
              <Button variant="contained" startIcon={<Edit />} onClick={() => openEdit(currentRow)}>
                Edit
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Create / Edit Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editId ? "Edit Announcement (Update + Re-send)" : "Create Announcement"}
          <IconButton
            onClick={() => setShowModal(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                  {TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                type="number"
                label="Priority"
                fullWidth
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                inputProps={{ min: 1, max: 10 }}
                helperText="Higher number = higher priority"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Display Position</InputLabel>
                <Select
                  value={displayPosition}
                  label="Display Position"
                  onChange={(e) => setDisplayPosition(e.target.value)}
                >
                  {DISPLAY_POSITIONS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>
                      {p.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Target</InputLabel>
                <Select
                  value={targetScope}
                  label="Target"
                  onChange={(e) => {
                    setTargetScope(e.target.value);
                    setSpecificRoles([]);
                    setSelectedUsers([]);
                  }}
                >
                  {TARGET_SCOPES.map((s) => (
                    <MenuItem
                      key={s.value}
                      value={s.value}
                      disabled={s.value === "all" && !canBroadcastAll}
                    >
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* SPECIFIC USERS */}
            {targetScope === "specific" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{specificPickerLabel}</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={fetchTargetUsers}
                    disabled={targetUsersLoading}
                  >
                    {targetUsersLoading ? "Loading..." : "Refresh List"}
                  </Button>
                </Stack>

                <Autocomplete
                  multiple
                  options={targetUsersOptions}
                  value={selectedUsers}
                  loading={targetUsersLoading}
                  disableCloseOnSelect
                  getOptionLabel={(opt) => `${opt.username} (${opt.role})`}
                  isOptionEqualToValue={(a, b) => a._id === b._id}
                  onChange={(e, val) => setSelectedUsers(val)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option._id}>
                      <Checkbox checked={selected} sx={{ mr: 1 }} />
                      {option.username}{" "}
                      <Typography sx={{ ml: 1 }} color="text.secondary">
                        ({option.role})
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select users"
                      placeholder="Search user..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {targetUsersLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Alert severity="info" sx={{ mt: 1 }}>
                  <b>sub_admin</b> can select only Dealers under him. <b>dealer</b> can select only Users under him.
                </Alert>
              </Grid>
            )}

            {/* HIERARCHY ROLES */}
            {targetScope === "hierarchy" && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Restrict to roles (optional)
                </Typography>
                <Stack direction="row" flexWrap="wrap">
                  {allowedRoles.map((r) => (
                    <FormControlLabel
                      key={r}
                      sx={{ mr: 2 }}
                      control={
                        <Checkbox checked={specificRoles.includes(r)} onChange={() => toggleRole(r)} />
                      }
                      label={r}
                    />
                  ))}
                </Stack>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                type="datetime-local"
                label="Start At"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                type="datetime-local"
                label="End At"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity={editId ? "warning" : "info"}>
                {editId ? (
                  <>
                    On <b>Update</b> we will: (1) Update record via <b>PUT /:announcementId</b> and then
                    (2) <b>Re-send</b> via <b>POST /trigger</b>.
                  </>
                ) : (
                  <>
                    Mobile app will show this announcement based on <b>Display Position</b>.
                    Backend must store <b>displayPosition</b> and return it in <b>/active</b>.
                  </>
                )}
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setShowModal(false);
              setError("");
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : editId ? "Update + Re-send" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <BrandConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Announcement"
        message={`Delete "${deleteRow?.title || "this announcement"}"?`}
        description={deleteRow?.message || "This announcement and all related data will be permanently deleted."}
        confirmLabel="Yes, Delete"
        tone="red"
      />
    </Box>
  );
}
